import React from 'react';

// Sticky sidebar: what will be created, what's blocking it, and the button that does it.

function Stat({ label, value, sub }) {
  return (
    <div className="flex items-baseline justify-between py-1.5">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-semibold tabular-nums text-gray-900">
        {value}
        {sub && <span className="ml-1 font-normal text-gray-400">{sub}</span>}
      </span>
    </div>
  );
}

export default function SummaryPanel({
  quote, options, blockers, warnings, onCreate, creating, result,
  serverPlan, planBusy, onRetry,
}) {
  const tasks = quote.modules.flatMap((m) => m.tasks);
  const activities = tasks.flatMap((t) => t.activities);
  const hours = activities.reduce((sum, a) => sum + (a.hours || 0), 0);
  const rate = quote.hourlyRate || 0;
  const totals = serverPlan?.totals;
  const creationErrors = result?.result?.errors || [];
  const missingBudgets = !!result &&
    (result.result?.budgetLines?.length || 0) < (result.plan?.tasks || 0);
  const completed = !!result && creationErrors.length === 0;

  return (
    <div className="sticky top-6 space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-base font-semibold text-gray-900">Will create</h2>

        <div className="divide-y divide-gray-100">
          <Stat label="Project" value="1" sub={options.templateId} />
          <Stat label="Project tasks" value={totals?.tasks ?? tasks.length} />
          <Stat label="Task activities" value={totals?.activities ?? activities.length} />
          <Stat label="Total hours" value={totals?.hours ?? hours} sub="h" />
          {rate > 0 && (
            <Stat label="At $" value={(hours * rate).toLocaleString()} sub={`(${rate}/hr)`} />
          )}
        </div>

        <p className="mt-3 border-t border-gray-100 pt-3 text-xs leading-relaxed text-gray-400">
          Activities are created with no time logged. Users record actual time against
          them later, from the calendar app or Acumatica.
        </p>
        <p className="mt-2 text-xs text-gray-400">
          {planBusy
            ? 'Refreshing server preview…'
            : serverPlan
              ? 'Verified against the server plan.'
              : 'Complete the required settings to verify the server plan.'}
        </p>
      </div>

      {blockers.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <h3 className="mb-2 text-sm font-semibold text-red-800">Before you can create</h3>
          <ul className="space-y-1 text-sm text-red-700">
            {blockers.map((b, i) => <li key={i}>• {b}</li>)}
          </ul>
        </div>
      )}

      {warnings.length > 0 && (
        <details className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <summary className="cursor-pointer text-sm font-semibold text-amber-800">
            {warnings.length} warning{warnings.length === 1 ? '' : 's'}
          </summary>
          <ul className="mt-2 space-y-1 text-sm text-amber-700">
            {warnings.map((w, i) => <li key={i}>• {w}</li>)}
          </ul>
        </details>
      )}

      <button
        type="button"
        disabled={blockers.length > 0 || creating || planBusy || !serverPlan || completed}
        onClick={() => onCreate()}
        className="w-full rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm
                   transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
      >
        {creating ? 'Creating in Acumatica…' : completed ? 'Project created' : 'Create project in Acumatica'}
      </button>

      {result && (
        <div className={`rounded-xl border p-4 ${result.result?.errors?.length ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}`}>
          <h3 className="mb-2 text-sm font-semibold text-gray-900">
            Created {result.result?.projectId}
          </h3>
          <p className="text-sm text-gray-700">
            {result.result?.tasks?.length} tasks, {result.result?.activities?.length} activities
          </p>
          {result.projectUrl && (
            <a
              href={result.projectUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-block text-sm font-medium text-blue-700 underline"
            >
              Open project in Acumatica
            </a>
          )}
          {(creationErrors.length > 0 || missingBudgets) && (
            <>
              {creationErrors.length > 0 && (
                <ul className="mt-2 space-y-1 text-sm text-amber-800">
                  {creationErrors.map((e, i) => <li key={i}>• {e}</li>)}
                </ul>
              )}
              {missingBudgets && (
                <p className="mt-2 text-sm text-amber-800">
                  {(result.plan?.tasks || 0) - (result.result?.budgetLines?.length || 0)} revenue budget row(s) still need syncing.
                </p>
              )}
              <button
                type="button"
                disabled={creating}
                onClick={onRetry}
                className="mt-3 w-full rounded-lg bg-amber-700 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-800 disabled:bg-gray-300"
              >
                {creating
                  ? 'Syncing missing items…'
                  : creationErrors.length
                    ? 'Retry missing items'
                    : 'Sync missing revenue budgets'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
